import { useState } from "react";
import { PauseCircle } from "@phosphor-icons/react";

/**
 * Horizontal timeline of a bot's auto-pause events. User can flip between
 * a 7 / 30 / 90 day range to spot recurring dips. Dots are placed by
 * ``(now - at) / window_ms`` — older events sink to the left, newest to
 * the right. Events outside the window are hidden (count shown as "N in
 * range · M older" so nothing feels silently dropped).
 */
const RANGES = [
  { key: "7d",  days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
];

const RANGE_KEY = "stratos.pauseTimeline.range";

// LOW-band threshold — score below this triggers an auto-pause. Kept in
// sync with backend/bot_confidence.py so the timeline colour ramp mirrors
// how the engine actually decided to pause each event.
const LOW_THRESHOLD = 50;

/**
 * Colour ramp for a pause dot: pauses that fired *just* under the LOW
 * threshold (score 40-49) render in light amber so users can visually
 * distinguish borderline events from deep-LOW pauses (score < 30, deep
 * red). This makes recurring "edge-of-band" pauses stand out at a glance.
 */
function dotColor(score) {
  if (typeof score !== "number") return { fill: "#f59e0b", stroke: "#f59e0b" }; // amber-500 fallback
  if (score >= 40) return { fill: "#fbbf24", stroke: "#fbbf24" }; // amber-400 — borderline
  if (score >= 30) return { fill: "#f59e0b", stroke: "#f59e0b" }; // amber-500 — mid LOW
  return { fill: "#dc2626", stroke: "#dc2626" }; // red-600 — deep LOW
}

export default function BotPauseTimeline({ symbol, events }) {
  const [rangeKey, setRangeKey] = useState(() => {
    try { return localStorage.getItem(RANGE_KEY) || "30d"; } catch { return "30d"; }
  });
  if (!Array.isArray(events) || events.length === 0) return null;

  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
  const now = Date.now();
  const window_ms = range.days * 24 * 60 * 60 * 1000;

  const enriched = events.map((ev, idx) => {
    const t = new Date(ev.at).getTime();
    const ago = now - t;
    return { ...ev, idx, t, ago };
  });
  const inRange = enriched
    .filter((p) => p.ago >= 0 && p.ago <= window_ms)
    .map((p) => ({ ...p, pct: 100 - (p.ago / window_ms) * 100 }));
  const older = enriched.length - inRange.length;
  const mostRecent = enriched.reduce((a, b) => (b.t > a.t ? b : a), enriched[0]);

  const changeRange = (v) => {
    setRangeKey(v);
    try { localStorage.setItem(RANGE_KEY, v); } catch {}
  };

  return (
    <div
      data-testid={`bot-pause-timeline-${symbol}`}
      className="mt-2 border border-amber-500/20 rounded-sm px-3 py-2.5 bg-amber-500/[0.03]"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="mono text-[9px] uppercase tracking-widest text-amber-400/80 flex items-center gap-1">
          <PauseCircle size={9} weight="fill" /> Pause timeline
        </span>
        <div className="flex items-center gap-2">
          <span className="mono text-[9px] text-zinc-500 uppercase tracking-widest">
            {inRange.length} in range{older > 0 ? ` · ${older} older` : ""}
          </span>
          <div
            data-testid={`pause-timeline-range-${symbol}`}
            role="tablist"
            className="flex items-center border border-zinc-800 rounded-sm overflow-hidden"
          >
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={rangeKey === r.key}
                data-testid={`pause-range-${symbol}-${r.key}`}
                onClick={() => changeRange(r.key)}
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 transition-colors ${
                  rangeKey === r.key
                    ? "bg-amber-500/30 text-amber-100"
                    : "text-zinc-500 hover:text-white hover:bg-zinc-800"
                }`}
              >
                {r.key}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="relative h-10">
        <div className="absolute left-0 right-0 top-1/2 h-px bg-zinc-800" />
        <div className="absolute left-0 -bottom-0.5 mono text-[9px] text-zinc-600">{range.days}d ago</div>
        <div className="absolute right-0 -bottom-0.5 mono text-[9px] text-zinc-600">now</div>
        {inRange.map((p) => {
          const score = typeof p.confidence_score === "number"
            ? `score ${Math.round(p.confidence_score)}` : "";
          const when = new Date(p.at).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
          });
          const { fill } = dotColor(p.confidence_score);
          return (
            <div
              key={`${p.at}-${p.idx}`}
              data-testid={`bot-pause-dot-${symbol}-${p.idx}`}
              data-score-tier={
                typeof p.confidence_score !== "number"
                  ? "unknown"
                  : p.confidence_score >= 40 ? "borderline"
                  : p.confidence_score >= 30 ? "mid" : "deep"
              }
              title={`${when} · LOW confidence${score ? " · " + score : ""}`}
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${p.pct}%` }}
            >
              <div
                className="w-2.5 h-2.5 rounded-full ring-2 ring-black/60 hover:scale-125 transition-transform"
                style={{ background: fill }}
              />
            </div>
          );
        })}
        {inRange.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center mono text-[10px] text-zinc-600">
            No pauses in the last {range.days} days
          </div>
        )}
      </div>
      {mostRecent && (
        <div className="mono text-[10px] text-zinc-500 mt-1">
          Most recent · {new Date(mostRecent.at).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
          })}
          {typeof mostRecent.confidence_score === "number"
            ? ` · score ${Math.round(mostRecent.confidence_score)}` : ""}
        </div>
      )}
      {/* Colour ramp key — helps users spot recurring "edge-of-band" pauses
          (light amber dots hugging the {LOW_THRESHOLD} threshold) vs deep-LOW
          events (red). Only shown when the range contains at least one
          scored dot so it doesn't clutter empty windows. */}
      {inRange.some((p) => typeof p.confidence_score === "number") && (
        <div
          data-testid={`pause-timeline-legend-${symbol}`}
          className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 pt-2 border-t border-zinc-800/60"
        >
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
            Threshold &lt; {LOW_THRESHOLD}
          </div>
          <LegendKey color="#fbbf24" label="40 – 49 borderline" testId={`pause-key-borderline-${symbol}`} />
          <LegendKey color="#f59e0b" label="30 – 39 mid" testId={`pause-key-mid-${symbol}`} />
          <LegendKey color="#dc2626" label="< 30 deep" testId={`pause-key-deep-${symbol}`} />
        </div>
      )}
    </div>
  );
}

function LegendKey({ color, label, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <span className="w-2 h-2 rounded-full ring-1 ring-black/60" style={{ background: color }} />
      <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
    </div>
  );
}
